import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { MinMaxStep, SectionChange, SectionModelType } from '../../types/section.type';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { SectionBlock } from '../section-block/section-block';
import { Duration } from '../../types/duration.type';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { SectionsEditorService } from '../../services/sections-editor.service';
import { durationToSeconds, formatTime } from '../../utils/duration.utils';
import {
  DEFAULT_TICKS_COUNT,
  MAX_TIME_TICKS,
  MIN_TICK_SPACING_PERCENT,
  MIN_TICK_SPACING_PX,
  MINIMAL_WORKOUT_DURATION,
  TIME_TICK_INTERVALS,
  WORKOUT_MODE_TICKS_COUNT,
} from '../../configs/sections-editor/section-chart.config';
import { ColorService } from '../../services/color.service';

@Component({
  selector: 'app-section-chart',
  imports: [CdkDropList, SectionBlock, IconComponent, CdkDrag],
  templateUrl: './section-chart.html',
  styleUrl: './section-chart.scss',
})
export class SectionChart {
  sectionsEditorService: SectionsEditorService = inject(SectionsEditorService);
  colorService: ColorService = inject(ColorService);
  sections: ModelSignal<SectionModelType[]> = model.required<SectionModelType[]>();
  mode: InputSignal<WorkoutMode | undefined> = input.required<WorkoutMode | undefined>();
  createSection: OutputEmitterRef<void> = output<void>();
  duplicateSections: OutputEmitterRef<void> = output<void>();
  minimalWorkoutDuration: number = MINIMAL_WORKOUT_DURATION;
  timebarDuration: WritableSignal<number> = signal(this.minimalWorkoutDuration);
  disabled: InputSignal<boolean> = input<boolean>(false);
  sectionChangeStart: OutputEmitterRef<void> = output<void>();
  sectionChange: OutputEmitterRef<{ sectionId: number; change: SectionChange }> = output<{
    sectionId: number;
    change: SectionChange;
  }>();
  sectionChangeEnd: OutputEmitterRef<void> = output<void>();

  /**
   * Echelle du temps figee pendant un resize : sinon, quand la duree totale
   * depasse la timebar, le graphe se re-echelonne sous le curseur.
   */
  private frozenTimelineDuration: WritableSignal<number | null> = signal(null);

  /** Largeur de l'axe du temps en px, `0` tant qu'elle n'est pas mesuree. */
  private readonly timebarWidth: WritableSignal<number> = signal(0);

  constructor() {
    if (typeof ResizeObserver === 'undefined') return;
    const observer: ResizeObserver = new ResizeObserver(([entry]: ResizeObserverEntry[]) =>
      this.timebarWidth.set(entry.contentRect.width),
    );
    effect(() => {
      observer.disconnect();
      observer.observe(this.timelineRef().nativeElement);
    });
    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }

  drop(event: CdkDragDrop<unknown>) {
    const reordered: SectionModelType[] = [...this.sections()];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.sections.set(reordered);
  }

  private totalDuration: Signal<number> = computed(() =>
    this.sections().reduce(
      (total: number, section: SectionModelType) => total + durationToSeconds(section.duration),
      0,
    ),
  );

  timelineDuration: Signal<number> = computed(
    () => this.frozenTimelineDuration() ?? Math.max(this.timebarDuration(), this.totalDuration()),
  );

  onSectionChangeStart(): void {
    this.frozenTimelineDuration.set(this.timelineDuration());
    this.sectionChangeStart.emit();
  }

  onSectionChangeEnd(): void {
    this.frozenTimelineDuration.set(null);
    this.sectionChangeEnd.emit();
  }

  ticks: Signal<number[]> = computed(() =>
    this.ticksBetween(this.sectionsEditorService.min(), this.sectionsEditorService.max()),
  );

  /**
   * Graduations de l'axe de droite (2e valeur du mode). Meme nombre de ticks que
   * l'axe de gauche : les deux axes partagent les lignes de la grille.
   */
  secondaryTicks: Signal<number[]> = computed(() => {
    const bounds: Readonly<MinMaxStep> | undefined =
      this.sectionsEditorService.secondaryMinMaxStep();
    return bounds ? this.ticksBetween(bounds.min, bounds.max) : [];
  });

  private ticksBetween(min: number, max: number): number[] {
    const ticks: number[] = [];
    const step: number = (max - min) / this.ticksCount;
    for (let index = 0; index <= this.ticksCount; index++) {
      ticks.push(Math.floor(step * index + min));
    }
    return ticks.reverse();
  }

  /**
   * Graduations de l'axe du temps, sur la meme echelle que les blocs
   * (`timelineDuration`), mais seulement sur la duree des blocs : de 0 a
   * `totalDuration`, qui a toujours sa graduation.
   *
   * L'intervalle est le plus petit "joli" pas qui donne au plus `maxTimeTicks`
   * intervalles et laisse au moins `MIN_TICK_SPACING_PX` entre deux libelles
   * (`minTickSpacingPercent` tant que la largeur n'est pas mesuree).
   */
  timeTicks: Signal<{ seconds: number; percent: number; label: string }[]> = computed(() => {
    const total: number = this.totalDuration();
    if (!total) return [];
    const timeline: number = this.timelineDuration();
    const toPercent = (seconds: number): number => (seconds / timeline) * 100;
    const width: number = this.timebarWidth();
    const minSpacing: number = width
      ? Math.max(this.minTickSpacingPercent, (MIN_TICK_SPACING_PX / width) * 100)
      : this.minTickSpacingPercent;
    const interval: number =
      this.timeTickIntervals.find(
        (interval: number) =>
          total / interval <= this.maxTimeTicks && toPercent(interval) >= minSpacing,
      ) ?? this.timeTickIntervals[this.timeTickIntervals.length - 1];

    const seconds: number[] = [];
    for (let tick = 0; tick < total; tick += interval) seconds.push(tick);
    while (seconds.length && toPercent(total - seconds[seconds.length - 1]) < minSpacing) {
      seconds.pop();
    }
    seconds.push(total);

    return seconds.map((tick: number) => ({
      seconds: tick,
      percent: toPercent(tick),
      label: formatTime(tick),
    }));
  });

  private readonly maxTimeTicks: number = MAX_TIME_TICKS;
  private readonly minTickSpacingPercent: number = MIN_TICK_SPACING_PERCENT;
  private readonly timeTickIntervals: readonly number[] = TIME_TICK_INTERVALS;

  timebarRef: Signal<ElementRef<HTMLDivElement>> = viewChild.required('timebar', {
    read: ElementRef<HTMLDivElement>,
  });

  private timelineRef: Signal<ElementRef<HTMLDivElement>> = viewChild.required('timeline', {
    read: ElementRef<HTMLDivElement>,
  });

  private get ticksCount(): number {
    const mode: WorkoutMode | undefined = this.sectionsEditorService.workoutMode();
    return mode ? WORKOUT_MODE_TICKS_COUNT[mode] : DEFAULT_TICKS_COUNT;
  }

  getBlockWidth(duration: Duration): number {
    const secondsDuration: number = durationToSeconds(duration);
    const percentSize: number = (secondsDuration * 100) / this.timebarDuration();
    const timebarWidth: number = this.timebarRef().nativeElement.clientWidth;
    return (percentSize * timebarWidth) / 100;
  }
}
